import React from "react";
import { NeoInput } from "../../../../../components/Common";
import { DEFAULT_RUNNING_CYCLE_PLAN, RUNNING_CYCLE_PHASES } from "../../domain/running/constants";
import RunningHelpInfo from "./RunningHelpInfo";

const RunningCycleSettings = ({ session = {}, namePrefix, handleChange, setFieldValue }) => {
    const cyclePlan = {
        ...DEFAULT_RUNNING_CYCLE_PLAN,
        ...(session.cyclePlan || {}),
    };

    const updateCycleField = (field, value) => {
        setFieldValue(`${namePrefix}.cyclePlan.${field}`, value);
    };

    return (
        <div className="running-cycle-settings mb-3">
            <div className="running-collapsible-header mb-3">
                <div>
                    <h6 className="mb-0 fw-bold text-dark font-size-13 d-flex align-items-center gap-2">
                        Ciclo e Prova
                        <span className="running-section-chip">Avançado</span>
                    </h6>
                    <span className="text-muted font-size-11">Use quando a sessão fizer parte de uma preparação com prova ou meta definida</span>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="form-check form-switch mb-0">
                        <input
                            type="checkbox"
                            role="switch"
                            className="form-check-input cursor-pointer"
                            id={`${namePrefix}.cyclePlan.enabled`}
                            checked={!!cyclePlan.enabled}
                            onChange={(event) => {
                                updateCycleField("enabled", event.target.checked);
                            }}
                        />
                        <label className="form-check-label font-size-12 fw-semibold text-dark cursor-pointer ms-1" htmlFor={`${namePrefix}.cyclePlan.enabled`}>
                            Planejar ciclo
                        </label>
                    </div>
                </div>
            </div>

            {cyclePlan.enabled && (
                <>
                    <div className="row g-2">
                        <div className="col-md-4 col-12">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Prova / objetivo <RunningHelpInfo text="Nome do evento ou meta que orienta o ciclo, como 10K, meia maratona ou retorno." /></label>
                            <NeoInput type="text" name={`${namePrefix}.cyclePlan.raceName`} value={cyclePlan.raceName || ""} onChange={handleChange} size="sm" placeholder="Ex: Meia Maratona de Maceió" />
                        </div>
                        <div className="col-md-2 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Data <RunningHelpInfo text="Data da prova ou objetivo. Ajuda a controlar semanas restantes e fase do ciclo." /></label>
                            <NeoInput type="date" name={`${namePrefix}.cyclePlan.raceDate`} value={cyclePlan.raceDate || ""} onChange={handleChange} size="sm" />
                        </div>
                        <div className="col-md-2 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Distância <RunningHelpInfo text="Distância alvo do objetivo, como 5K, 10K, 21K, 42K ou trail." /></label>
                            <NeoInput type="text" name={`${namePrefix}.cyclePlan.raceDistance`} value={cyclePlan.raceDistance || ""} onChange={handleChange} size="sm" placeholder="10K, 21K..." />
                        </div>
                        <div className="col-md-2 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Semana <RunningHelpInfo text="Semana atual dentro do planejamento. Exemplo: semana 4 de 12." /></label>
                            <NeoInput type="number" name={`${namePrefix}.cyclePlan.weekNumber`} value={cyclePlan.weekNumber || ""} onChange={handleChange} size="sm" min="1" placeholder="4" />
                        </div>
                        <div className="col-md-2 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Total <RunningHelpInfo text="Quantidade total de semanas do ciclo." /></label>
                            <NeoInput type="number" name={`${namePrefix}.cyclePlan.totalWeeks`} value={cyclePlan.totalWeeks || ""} onChange={handleChange} size="sm" min="1" placeholder="12" />
                        </div>
                        <div className="col-md-3 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Fase <RunningHelpInfo text="Momento do ciclo: base, construção, específica, polimento, prova ou recuperação." /></label>
                            <NeoInput type="select" name={`${namePrefix}.cyclePlan.phase`} value={cyclePlan.phase || "base"} onChange={handleChange} size="sm">
                                {RUNNING_CYCLE_PHASES.map(phase => (
                                    <option key={phase.value} value={phase.value}>{phase.label}</option>
                                ))}
                            </NeoInput>
                        </div>
                        <div className="col-md-2 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Km semana <RunningHelpInfo text="Volume total planejado para a semana inteira, não apenas para este treino." /></label>
                            <NeoInput type="number" name={`${namePrefix}.cyclePlan.plannedWeeklyKm`} value={cyclePlan.plannedWeeklyKm || ""} onChange={handleChange} size="sm" min="0" step="0.5" />
                        </div>
                        <div className="col-md-2 col-6">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Km anterior <RunningHelpInfo text="Volume da semana anterior. Serve para comparar aumento ou redução de carga." /></label>
                            <NeoInput type="number" name={`${namePrefix}.cyclePlan.previousWeeklyKm`} value={cyclePlan.previousWeeklyKm || ""} onChange={handleChange} size="sm" min="0" step="0.5" />
                        </div>
                        <div className="col-md-5 col-12">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Distribuição de intensidade <RunningHelpInfo text="Percentual da semana em intensidade leve, moderada e forte. Exemplo clássico: 80/10/10." /></label>
                            <div className="running-cycle-intensity-grid">
                                <NeoInput type="number" name={`${namePrefix}.cyclePlan.intensityEasyPercent`} value={cyclePlan.intensityEasyPercent || ""} onChange={handleChange} size="sm" min="0" max="100" />
                                <NeoInput type="number" name={`${namePrefix}.cyclePlan.intensityModeratePercent`} value={cyclePlan.intensityModeratePercent || ""} onChange={handleChange} size="sm" min="0" max="100" />
                                <NeoInput type="number" name={`${namePrefix}.cyclePlan.intensityHardPercent`} value={cyclePlan.intensityHardPercent || ""} onChange={handleChange} size="sm" min="0" max="100" />
                            </div>
                            <small className="text-muted font-size-10 mt-1 d-block">Leve / moderado / forte</small>
                        </div>
                        <div className="col-md-3 col-6 d-flex align-items-end">
                            <div className="form-check mb-2">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`${namePrefix}.cyclePlan.deloadWeek`}
                                    checked={!!cyclePlan.deloadWeek}
                                    onChange={(event) => updateCycleField("deloadWeek", event.target.checked)}
                                />
                                <label className="form-check-label font-size-12 fw-semibold text-dark cursor-pointer ms-1" htmlFor={`${namePrefix}.cyclePlan.deloadWeek`}>
                                    Semana regenerativa <RunningHelpInfo text="Marque quando a semana tem redução de volume/intensidade para recuperação." />
                                </label>
                            </div>
                        </div>
                        <div className="col-12">
                            <label className="form-label font-size-10 fw-bold text-uppercase text-muted mb-1">Observações do ciclo <RunningHelpInfo text="Contexto da semana, ajustes de carga, restrições, foco técnico ou estratégia da fase." /></label>
                            <NeoInput type="text" name={`${namePrefix}.cyclePlan.notes`} value={cyclePlan.notes || ""} onChange={handleChange} size="sm" placeholder="Contexto da semana, ajuste de volume, foco da fase..." />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default RunningCycleSettings;
